/**
 * `PreChatForm` — collects visitor details before a session is created
 * (ARCHITECTURE §7). The field set is driven by `WidgetConfig.preChatForm`
 * (`FormFieldConfig[]`); fields render generically and the collected
 * `PreChatFormValues` go to `onSubmit`.
 *
 * Visual language (2026 refresh): a warm greeting block, icon-prefixed inputs on
 * soft bordered fields, an integrated phone group, and a raised primary CTA —
 * all driven by the `--lc-*` theme tokens so org branding applies untouched.
 *
 * When the org enables the GDPR consent gate (`config.gdpr.enabled`), the
 * {@link ConsentGate} is shown FIRST; only after the visitor agrees (recorded via
 * the public consent endpoint) does the normal pre-chat form render. Consent is
 * tracked in component state — per widget session, which is all GDPR requires here.
 */

import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import type { PreChatFormValues, WidgetConfig } from '@flozic/shared/types';
import { ConsentGate } from './ConsentGate';
import { COUNTRIES, DEFAULT_COUNTRY_ISO2, flagEmoji, type CountryDialCode } from '../data/countries';

export interface PreChatFormProps {
  config: WidgetConfig | null;
  onSubmit: (values: PreChatFormValues) => void;
  submitting?: boolean;
  /** A proactive greeting from a matched trigger, shown as a highlighted bubble atop the form. */
  proactiveMessage?: string | null;
}

/* ── Field icons (stroke inherits currentColor; wrapper sets the muted tone) ── */

const _ICON_CLS = 'h-[18px] w-[18px]';

function UserIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className={_ICON_CLS} aria-hidden>
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className={_ICON_CLS} aria-hidden>
      <rect x="2" y="4" width="20" height="16" rx="3" />
      <path d="m22 7-10 6L2 7" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className={_ICON_CLS} aria-hidden>
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}

function ChatIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className={_ICON_CLS} aria-hidden>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function ListIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className={_ICON_CLS} aria-hidden>
      <path d="M8 6h13M8 12h13M8 18h13" />
      <circle cx="3.5" cy="6" r="0.5" fill="currentColor" />
      <circle cx="3.5" cy="12" r="0.5" fill="currentColor" />
      <circle cx="3.5" cy="18" r="0.5" fill="currentColor" />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4" aria-hidden>
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

function ChevronDownIcon({ className = 'h-3.5 w-3.5' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

/** Checkmark affordance for the currently-selected row in the country picker's list. */
function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5" aria-hidden>
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

/** Icon for a field, by its type (name-ish text → person; email → mail; etc.). */
function fieldIcon(type: string): ReactNode {
  switch (type) {
    case 'email':
      return <MailIcon />;
    case 'tel':
      return <PhoneIcon />;
    case 'textarea':
      return <ChatIcon />;
    case 'select':
      return <ListIcon />;
    default:
      return <UserIcon />;
  }
}

/* ── Shared field chrome ────────────────────────────────────────────────────── */

const FIELD_SHELL =
  'group flex items-stretch overflow-hidden rounded-xl border border-black/10 bg-lc-bg shadow-sm ' +
  'transition-all duration-150 focus-within:border-lc-primary focus-within:ring-2 focus-within:ring-lc-primary/25';

const INPUT_CLS =
  'min-w-0 flex-1 bg-transparent px-3 py-2.5 text-[15px] text-lc-text outline-none placeholder:text-lc-text/35';

/** Leading icon slot inside a field shell (muted; tints to primary when the field has focus). */
function IconSlot({ children }: { children: ReactNode }) {
  return (
    <span className="flex items-center pl-3.5 text-lc-text/35 transition-colors duration-150 group-focus-within:text-lc-primary">
      {children}
    </span>
  );
}

/** Field label: small, confident, with a primary-tinted required mark. */
function FieldLabel({ label, required }: { label: string; required?: boolean }) {
  return (
    <span className="px-0.5 text-[12.5px] font-semibold tracking-wide text-lc-text/65">
      {label}
      {required ? <span className="ml-0.5 text-lc-primary">*</span> : null}
    </span>
  );
}

const DEFAULT_COUNTRY: CountryDialCode =
  COUNTRIES.find((c) => c.iso2 === DEFAULT_COUNTRY_ISO2) ?? COUNTRIES[0];

/* ── Boundary-aware popover placement ──────────────────────────────────────────
 * The widget iframe is a fixed 400×640 on desktop and full-screen on mobile
 * (`loader/loader.ts`'s `EXPANDED`), and the phone field's position within the
 * pre-chat form is org-configurable (`config.preChatForm` field order) — so the
 * popover can't just always open downward, or an org whose form puts phone in
 * the lower half would have it clipped by the iframe's own bounds with nothing
 * to scroll to reveal it. `computePopoverPlacement` is a pure function of the
 * trigger's rect + viewport size (no DOM reads of its own) so the boundary math
 * is unit-testable in isolation — see `.superpowers/fix/countries-report.md`
 * for the worked examples, including the "tight on both sides" edge case. */

/** Fixed popover width (px); clamped down for very narrow viewports. */
const POPOVER_WIDTH = 288;
/** Visual gap between the trigger and the popover. */
const POPOVER_GAP = 6;
/** Minimum clearance kept from every viewport edge. */
const POPOVER_EDGE = 8;
/** Floor: search row + ~3 result rows — below this the panel stops being usable. */
const POPOVER_MIN_HEIGHT = 160;
/** Ceiling: keep the panel proportioned to a chat widget even in a tall embed. */
const POPOVER_MAX_HEIGHT = 320;

interface PopoverPlacement {
  top: number;
  left: number;
  width: number;
  maxHeight: number;
}

/**
 * Flips the popover upward when there isn't room below, clamps it horizontally so it never
 * runs off either edge, and clamps its max-height to whatever space is actually available.
 *
 * When space is tight on BOTH sides (only possible in a pathologically short viewport), the
 * `POPOVER_MIN_HEIGHT` floor wins over the measured available space, and the final vertical
 * clamp keeps the whole panel inside the viewport — which can mean it slightly overlaps the
 * trigger button. That's a deliberate tradeoff: a panel that covers its trigger but stays fully
 * on-screen, scrollable, and keyboard-usable beats one that's clipped, off-screen, or a
 * zero-height sliver.
 */
function computePopoverPlacement(
  trigger: { top: number; bottom: number; left: number },
  viewport: { width: number; height: number },
): PopoverPlacement {
  const spaceBelow = viewport.height - trigger.bottom - POPOVER_GAP - POPOVER_EDGE;
  const spaceAbove = trigger.top - POPOVER_GAP - POPOVER_EDGE;

  // Prefer opening downward (natural reading order); flip up only when downward can't fit even
  // the floor AND upward genuinely has more room to offer.
  const openUpward = spaceBelow < POPOVER_MIN_HEIGHT && spaceAbove > spaceBelow;
  const available = openUpward ? spaceAbove : spaceBelow;
  const maxHeight = Math.min(POPOVER_MAX_HEIGHT, Math.max(POPOVER_MIN_HEIGHT, available));

  const width = Math.min(POPOVER_WIDTH, viewport.width - POPOVER_EDGE * 2);
  const left = Math.max(
    POPOVER_EDGE,
    Math.min(trigger.left, viewport.width - width - POPOVER_EDGE),
  );

  const naturalTop = openUpward
    ? trigger.top - POPOVER_GAP - maxHeight
    : trigger.bottom + POPOVER_GAP;
  const top = Math.max(
    POPOVER_EDGE,
    Math.min(naturalTop, viewport.height - maxHeight - POPOVER_EDGE),
  );

  return { top, left, width, maxHeight };
}

/**
 * Country-code picker for `PhoneField` — a button showing the selected flag + dial code that
 * opens a searchable list (all 240+ `COUNTRIES`, filtered by name OR dial code). Rendered via a
 * portal into `document.body` (still the widget iframe's own document — the widget always runs
 * isolated in its own iframe, ARCHITECTURE §7) so the floating panel escapes the pre-chat
 * screen's `overflow-hidden` ancestors instead of being clipped mid-list, with its own placement
 * clamped to the iframe viewport by `computePopoverPlacement` above.
 *
 * Keyboard model: focus stays on the search input the entire time the popover is open (a "roving
 * virtual focus" combobox, per the ARIA APG listbox pattern) — option rows are real `<button>`s
 * for mouse/click support but carry `tabIndex={-1}` so Tab can never land on one. That's what
 * closes the original bug where tabbing into a row stranded the visitor with no working Escape:
 * with focus pinned to the input, Escape/Enter/Arrow keys always have somewhere correct to bind,
 * and `aria-activedescendant` tells assistive tech which row is "virtually" focused instead.
 */
function CountryPicker({
  selected,
  onSelect,
}: {
  selected: CountryDialCode;
  onSelect: (country: CountryDialCode) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [coords, setCoords] = useState<PopoverPlacement | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();
  const optionId = (iso2: string) => `${listboxId}-opt-${iso2}`;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter(
      (c) => c.name.toLowerCase().includes(q) || c.dialCode.includes(q) || c.iso2.toLowerCase() === q,
    );
  }, [query]);

  // Re-anchor the highlighted row to the top match whenever the query (or open state) changes,
  // so Enter always acts on what's actually on top of the filtered list.
  useEffect(() => {
    setActiveIndex(0);
  }, [query, open]);

  // On open: focus the search box (keyboard-first flow) and place the popover against the
  // trigger's current position, re-clamping on scroll/resize instead of just closing — the
  // panel should track the trigger and stay on-screen for as long as it's open.
  useEffect(() => {
    if (!open) return;
    const recompute = () => {
      const el = triggerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setCoords(
        computePopoverPlacement(
          { top: rect.top, bottom: rect.bottom, left: rect.left },
          { width: window.innerWidth, height: window.innerHeight },
        ),
      );
    };
    recompute();
    searchRef.current?.focus();
    window.addEventListener('scroll', recompute, true);
    window.addEventListener('resize', recompute);
    return () => {
      window.removeEventListener('scroll', recompute, true);
      window.removeEventListener('resize', recompute);
    };
  }, [open]);

  // Keep the highlighted row scrolled into view as arrow keys move it.
  useEffect(() => {
    if (!open) return;
    listRef.current
      ?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, open]);

  const close = (): void => {
    setOpen(false);
    setQuery('');
  };

  const choose = (country: CountryDialCode): void => {
    onSelect(country);
    close();
    triggerRef.current?.focus();
  };

  // Arrow/Enter — bound to the search input, which is where focus lives the whole time the
  // popover is open (see the roving-focus note above).
  const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const country = filtered[activeIndex];
      if (country) choose(country);
    }
  };

  // Escape — bound at the POPOVER CONTAINER level (not just the search input) so it always
  // works no matter which descendant a keydown bubbles up from, rather than depending on focus
  // staying exactly where we expect.
  const handlePopoverKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      triggerRef.current?.focus();
    }
  };

  const activeOption = filtered[activeIndex];

  return (
    <div className="shrink-0">
      <button
        type="button"
        ref={triggerRef}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-label={`Country code, currently ${selected.name} ${selected.dialCode}`}
        className="flex shrink-0 items-center gap-1 bg-transparent py-2.5 pl-2.5 pr-1.5 text-[15px] text-lc-text outline-none"
      >
        <span aria-hidden>{flagEmoji(selected.iso2)}</span>
        <span>{selected.dialCode}</span>
        <ChevronDownIcon className="h-3 w-3 text-lc-text/40" />
      </button>

      {open && coords
        ? createPortal(
            <>
              <div className="fixed inset-0 z-40" onClick={close} aria-hidden />
              <div
                style={{
                  top: coords.top,
                  left: coords.left,
                  width: coords.width,
                  maxHeight: coords.maxHeight,
                }}
                className="fixed z-50 flex flex-col overflow-hidden rounded-xl border border-black/10 bg-lc-bg shadow-xl"
                onKeyDown={handlePopoverKeyDown}
              >
                <div className="shrink-0 border-b border-black/10 p-1.5">
                  <input
                    ref={searchRef}
                    type="text"
                    role="combobox"
                    aria-autocomplete="list"
                    aria-expanded={open}
                    aria-controls={listboxId}
                    aria-activedescendant={activeOption ? optionId(activeOption.iso2) : undefined}
                    aria-label="Search country"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    placeholder="Search country or code…"
                    className="w-full rounded-lg bg-black/5 px-2.5 py-1.5 text-[13.5px] text-lc-text outline-none placeholder:text-lc-text/40"
                  />
                </div>
                <div
                  ref={listRef}
                  role="listbox"
                  id={listboxId}
                  aria-label="Country"
                  className="min-h-0 flex-1 overflow-y-auto py-1"
                >
                  {filtered.length === 0 ? (
                    <p className="px-3 py-2 text-[13px] text-lc-text/45">No matches</p>
                  ) : (
                    filtered.map((country, i) => {
                      const isSelected = country.iso2 === selected.iso2;
                      return (
                        <button
                          key={country.iso2}
                          id={optionId(country.iso2)}
                          type="button"
                          tabIndex={-1}
                          data-index={i}
                          role="option"
                          aria-selected={isSelected}
                          onMouseEnter={() => setActiveIndex(i)}
                          onClick={() => choose(country)}
                          className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13.5px] transition-colors ${
                            i === activeIndex ? 'bg-lc-primary/10' : 'hover:bg-black/5'
                          }`}
                        >
                          <span className="w-5 shrink-0 text-center" aria-hidden>
                            {flagEmoji(country.iso2)}
                          </span>
                          <span className="flex-1 truncate text-lc-text">{country.name}</span>
                          <span className="shrink-0 text-lc-text/45">{country.dialCode}</span>
                          <span className="w-3.5 shrink-0 text-lc-primary" aria-hidden>
                            {isSelected ? <CheckIcon /> : null}
                          </span>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            </>,
            document.body,
          )
        : null}
    </div>
  );
}

/** Phone input with a country-code selector; emits the combined `"{dialCode} {number}"` value. */
function PhoneField({ required, onChange }: { required?: boolean; onChange: (v: string) => void }) {
  const [country, setCountry] = useState<CountryDialCode>(DEFAULT_COUNTRY);
  const [number, setNumber] = useState('');
  const emit = (nextCountry: CountryDialCode, nextNumber: string) =>
    onChange(nextNumber.trim() ? `${nextCountry.dialCode} ${nextNumber.trim()}` : '');
  return (
    <div className={FIELD_SHELL}>
      <IconSlot>
        <PhoneIcon />
      </IconSlot>
      <CountryPicker
        selected={country}
        onSelect={(next) => {
          setCountry(next);
          emit(next, number);
        }}
      />
      <span className="my-2.5 w-px bg-black/10" aria-hidden />
      <input
        type="tel"
        inputMode="tel"
        required={required}
        placeholder="Phone number"
        className={INPUT_CLS}
        onChange={(e) => {
          setNumber(e.target.value);
          emit(country, e.target.value);
        }}
      />
    </div>
  );
}

export function PreChatForm({
  config,
  onSubmit,
  submitting = false,
  proactiveMessage = null,
}: PreChatFormProps) {
  const fields = config?.preChatForm ?? [];
  const hasFields = fields.length > 0;
  const [values, setValues] = useState<PreChatFormValues>({});
  // Consent gate: block the form behind the GDPR consent screen until agreed (this session).
  const [consented, setConsented] = useState(false);
  const gdpr = config?.gdpr;
  const gateOpen = !(gdpr?.enabled && !consented);

  // No fields configured → nothing for the visitor to fill in, so start the session
  // automatically once the consent gate (if any) has been passed. One-shot guard so a
  // re-render can't fire a duplicate startSession. Declared BEFORE the ConsentGate
  // early return to keep the hook order stable (rules of hooks).
  const autoStartedRef = useRef(false);
  useEffect(() => {
    if (gateOpen && !hasFields && !submitting && !autoStartedRef.current) {
      autoStartedRef.current = true;
      onSubmit({});
    }
  }, [gateOpen, hasFields, submitting, onSubmit]);

  if (gdpr?.enabled && !consented) {
    return <ConsentGate gdpr={gdpr} onAgree={() => setConsented(true)} />;
  }

  // Auto-start in flight — show a minimal loading state (matches WidgetApp's loader)
  // instead of flashing the intro copy + a lone "Start chat" button.
  if (!hasFields) {
    return (
      <div className="flex flex-1 items-center justify-center text-sm text-lc-text/50">
        Starting chat…
      </div>
    );
  }

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    onSubmit(values);
  };

  const setField = (name: string, value: string): void =>
    setValues((prev) => ({ ...prev, [name]: value }));

  return (
    <form onSubmit={handleSubmit} className="lc-rise flex flex-col gap-4 p-5">
      {proactiveMessage ? (
        <div className="rounded-2xl rounded-bl-md bg-lc-bubble-in px-3.5 py-2.5 text-sm text-lc-text shadow-sm">
          {proactiveMessage}
        </div>
      ) : null}

      {/* Greeting block — a friendly hello above the fields (welcome copy stays a chat bubble). */}
      <div className="flex flex-col gap-1 pt-1">
        <span className="text-[22px] leading-tight" aria-hidden>
          👋
        </span>
        <h2 className="text-lg font-bold leading-snug text-lc-text">
          {config?.preChatIntro?.trim() || 'Hi there!'}
        </h2>
        {!proactiveMessage && !config?.welcomeMessage?.trim() ? (
          <p className="text-[13.5px] leading-relaxed text-lc-text/55">
            Tell us a bit about you and we&apos;ll connect you with the team.
          </p>
        ) : null}
      </div>

      {config?.welcomeMessage?.trim() ? (
        <div className="rounded-2xl rounded-bl-md bg-lc-bubble-in px-3.5 py-2.5 text-sm text-lc-text shadow-sm">
          {config.welcomeMessage}
        </div>
      ) : null}

      <div className="flex flex-col gap-3.5">
        {fields.map((field) => (
          <label key={field.name} className="flex flex-col gap-1.5">
            <FieldLabel label={field.label} required={field.required} />
            {field.type === 'textarea' ? (
              <div className={FIELD_SHELL}>
                <span className="flex items-start pl-3.5 pt-3 text-lc-text/35 transition-colors duration-150 group-focus-within:text-lc-primary">
                  <ChatIcon />
                </span>
                <textarea
                  className={`${INPUT_CLS} min-h-[4.5rem] resize-y`}
                  required={field.required}
                  placeholder={`${field.label}…`}
                  onChange={(e) => setField(field.name, e.target.value)}
                />
              </div>
            ) : field.type === 'select' ? (
              <div className={FIELD_SHELL}>
                <IconSlot>{fieldIcon(field.type)}</IconSlot>
                <select
                  className={`${INPUT_CLS} cursor-pointer appearance-none`}
                  required={field.required}
                  defaultValue=""
                  onChange={(e) => setField(field.name, e.target.value)}
                >
                  <option value="" disabled>
                    Select…
                  </option>
                  {(field.options ?? []).map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
                <span className="flex items-center pr-3.5 text-lc-text/40" aria-hidden>
                  <ChevronDownIcon className="h-4 w-4" />
                </span>
              </div>
            ) : field.type === 'tel' ? (
              <PhoneField
                required={field.required}
                onChange={(value) => setField(field.name, value)}
              />
            ) : (
              <div className={FIELD_SHELL}>
                <IconSlot>{fieldIcon(field.type)}</IconSlot>
                <input
                  type={field.type}
                  className={INPUT_CLS}
                  required={field.required}
                  placeholder={field.label}
                  onChange={(e) => setField(field.name, e.target.value)}
                />
              </div>
            )}
          </label>
        ))}
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="mt-1 flex items-center justify-center gap-2 rounded-xl bg-lc-primary px-4 py-3 text-[15px] font-semibold text-lc-on-primary shadow-lg shadow-lc-primary/25 transition-all duration-150 hover:brightness-110 active:scale-[0.98] disabled:opacity-60 disabled:shadow-none"
      >
        {submitting ? (
          <>
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-lc-on-primary/40 border-t-lc-on-primary" aria-hidden />
            Starting…
          </>
        ) : (
          <>
            Start chat
            <SendIcon />
          </>
        )}
      </button>
      {/* TODO(phase 3): client-side required-field validation + error display. */}
    </form>
  );
}
